import { FormValidator } from '@app/calculators/effort-planner-form/form-validation/formValidator';
import { Validateable } from '@app/calculators/effort-planner-form/form-validation/validateable';
import { OrganisationRole, WithdrawalProductType } from '@generated/generatedEntities';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AccountDTO {
  activated: boolean;
  authorities: string[];
  createdBy: string;
  createdDate: Date;
  email: string;
  firstName: string;
  lastName: string;
  id: number;
  imageUrl: string;
  langKey: string;
  lastModifiedBy: string;
  lastModifiedDate: Date;
  login: string;
  organisationId: number;
  organisationRole: OrganisationRole;
  password: string;
  showPrivRefObject: boolean;
  showSysRefObject: boolean;
  withdrawalProductTypes: WithdrawalProductType[];
}

export interface AccountModelData {
  email: string | undefined;
  password: string | undefined;
  confirmPassword: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  organisationName: string | undefined;
  organisationStreet: string | undefined;
  organisationZipcode: string | undefined;
  organisationPlace: string | undefined;
  login: string | undefined;
  withdrawalProductTypes: WithdrawalProductType[];
}

export type AccountModelValidationField = 'email' | 'password' | 'confirmPassword' | 'firstName' | 'lastName';

export class AccountModel implements Validateable {
  private emailSubject$: BehaviorSubject<string | undefined>;
  private passwordSubject$: BehaviorSubject<string | undefined>;
  private confirmPasswordSubject$: BehaviorSubject<string | undefined>;
  private firstNameSubject$: BehaviorSubject<string | undefined>;
  private lastNameSubject$: BehaviorSubject<string | undefined>;
  private withdrawalProductTypesSubject$: BehaviorSubject<WithdrawalProductType[]>;
  private loginSubject$: BehaviorSubject<string | undefined>;
  readonly email$: Observable<string | undefined>;
  readonly password$: Observable<string | undefined>;
  readonly confirmPassword$: Observable<string | undefined>;
  readonly firstName$: Observable<string | undefined>;
  readonly lastName$: Observable<string | undefined>;
  readonly withdrawalProductTypes$: Observable<WithdrawalProductType[]>;
  readonly login$: Observable<string | undefined>;
  readonly formValidator = new FormValidator<AccountModelValidationField>();

  constructor(data: AccountModelData | undefined) {
    this.emailSubject$ = new BehaviorSubject<string | undefined>(data?.email);
    this.passwordSubject$ = new BehaviorSubject<string | undefined>(data?.password);
    this.confirmPasswordSubject$ = new BehaviorSubject<string | undefined>(data?.confirmPassword);
    this.firstNameSubject$ = new BehaviorSubject<string | undefined>(data?.firstName);
    this.lastNameSubject$ = new BehaviorSubject<string | undefined>(data?.lastName);
    this.withdrawalProductTypesSubject$ = new BehaviorSubject<WithdrawalProductType[]>(
      data?.withdrawalProductTypes ?? [],
    );
    this.loginSubject$ = new BehaviorSubject<string | undefined>(data?.login);
    this.email$ = this.emailSubject$.asObservable();
    this.password$ = this.passwordSubject$.asObservable();
    this.confirmPassword$ = this.confirmPasswordSubject$.asObservable();
    this.firstName$ = this.firstNameSubject$.asObservable();
    this.lastName$ = this.lastNameSubject$.asObservable();
    this.withdrawalProductTypes$ = this.withdrawalProductTypesSubject$.asObservable();
    this.login$ = this.loginSubject$.asObservable();
  }

  get email() {
    return this.emailSubject$.value;
  }
  set email(value: string | undefined) {
    this.emailSubject$.next(value);
  }

  get password() {
    return this.passwordSubject$.value;
  }
  set password(value: string | undefined) {
    this.passwordSubject$.next(value);
  }

  get firstName() {
    return this.firstNameSubject$.value;
  }
  set firstName(value: string | undefined) {
    this.firstNameSubject$.next(value);
  }

  get lastName() {
    return this.lastNameSubject$.value;
  }
  set lastName(value: string | undefined) {
    this.lastNameSubject$.next(value);
  }

  get withdrawalProductTypes() {
    return this.withdrawalProductTypesSubject$.value;
  }
  set withdrawalProductTypes(value: WithdrawalProductType[]) {
    this.withdrawalProductTypesSubject$.next(value);
  }

  get confirmPassword() {
    return this.confirmPasswordSubject$.value;
  }
  set confirmPassword(value: string | undefined) {
    this.confirmPasswordSubject$.next(value);
  }

  get login() {
    return this.loginSubject$.value;
  }
  set login(value: string | undefined) {
    this.loginSubject$.next(value);
  }

  isValid(): boolean {
    return this.formValidator.isValid;
  }
  resetValidations(): void {
    this.formValidator.reset();
  }
  validate(): void {
    this.formValidator.validate('email', 'global.messages.validate.email.required', (this.email?.length ?? 0) > 0);
    this.formValidator.validate(
      'email',
      'global.messages.validate.email.invalid',
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email || ''),
    );
    this.formValidator.validate(
      'password',
      'global.messages.validate.newpassword.required',
      (this.password?.length ?? 0) > 0,
    );
    this.formValidator.validate(
      'password',
      'global.messages.validate.newpassword.minlength',
      (this.password?.length ?? 0) > 4,
    );
    this.formValidator.validate(
      'password',
      'global.messages.validate.newpassword.maxlength',
      (this.password?.length ?? 0) < 50,
    );
    this.formValidator.validate(
      'confirmPassword',
      'global.messages.validate.confirmpassword.required',
      (this.confirmPassword?.length ?? 0) > 0,
    );
    this.formValidator.validate(
      'confirmPassword',
      'global.messages.validate.confirmpassword.minlength',
      (this.confirmPassword?.length ?? 0) > 4,
    );
    this.formValidator.validate(
      'confirmPassword',
      'global.messages.validate.confirmpassword.maxlength',
      (this.confirmPassword?.length ?? 0) < 50,
    );
    this.formValidator.validate(
      'confirmPassword',
      'global.messages.validate.confirmpassword.notfit',
      this.confirmPassword === this.password,
    );
    this.formValidator.validate(
      'firstName',
      'settings.messages.validate.firstname.required',
      (this.firstName?.length ?? 0) > 0,
    );
    this.formValidator.validate(
      'firstName',
      'settings.messages.validate.firstname.minlength',
      (this.firstName?.length ?? 0) >= 1,
    );
    this.formValidator.validate(
      'firstName',
      'settings.messages.validate.firstname.maxlength',
      (this.firstName?.length ?? 0) < 50,
    );
    this.formValidator.validate(
      'lastName',
      'settings.messages.validate.lastname.required',
      (this.lastName?.length ?? 0) > 0,
    );
    this.formValidator.validate(
      'lastName',
      'settings.messages.validate.lastname.minlength',
      (this.lastName?.length ?? 0) >= 1,
    );
    this.formValidator.validate(
      'lastName',
      'settings.messages.validate.lastname.maxlength',
      (this.lastName?.length ?? 0) < 50,
    );
  }

  toDto(): AccountDTO {
    // TODO: We should not allow default values to be set here.
    return {
      activated: true,
      authorities: ['ROLE_USER'],
      createdBy: '',
      createdDate: new Date(),
      email: this.email ?? '',
      password: this.password ?? '',
      firstName: this.firstName ?? '',
      lastName: this.lastName ?? '',
      id: -1,
      imageUrl: '',
      langKey: 'DE_CH',
      lastModifiedBy: '',
      lastModifiedDate: new Date(),
      login: '',
      organisationId: 0,
      organisationRole: 'ORG_ADMIN',
      showPrivRefObject: true,
      showSysRefObject: true,
      withdrawalProductTypes: [],
    };
  }
}
